/**
 * Document-type graphics in /public/assets/graphics (single asset for light/dark, like pdf-icon.svg).
 */
export const FILE_TYPE_GRAPHICS = {
  pdf: '/assets/graphics/pdf-icon.svg',
  docx: '/assets/graphics/docx_icon.svg',
  csv: '/assets/graphics/csv_icon.svg',
  xls: '/assets/graphics/xls_icon.svg',
} as const;

const GENERIC_DOC_DARK = '/assets/icons/document.on.document.svg';
const GENERIC_DOC_LIGHT = '/assets/icons/document.on.document.black.svg';

/**
 * Returns a graphics path for known office/document types; otherwise null (caller may use generic doc icon).
 */
export function fileTypeGraphicPath(
  filename: string,
  contentType?: string | null
): string | null {
  const fn = (filename || '').toLowerCase();
  const ct = (contentType || '').toLowerCase();

  if (ct.includes('pdf') || fn.endsWith('.pdf')) return FILE_TYPE_GRAPHICS.pdf;
  if (
    ct.includes('wordprocessingml') ||
    ct.includes('msword') ||
    fn.endsWith('.docx') ||
    fn.endsWith('.doc')
  ) {
    return FILE_TYPE_GRAPHICS.docx;
  }
  if (ct.includes('csv') || fn.endsWith('.csv')) return FILE_TYPE_GRAPHICS.csv;
  if (
    ct.includes('spreadsheetml') ||
    ct.includes('ms-excel') ||
    fn.endsWith('.xlsx') ||
    fn.endsWith('.xls')
  ) {
    return FILE_TYPE_GRAPHICS.xls;
  }
  return null;
}

/** Graphics path or generic document icon for chips / lists. */
export function fileTypeGraphicPathOrGeneric(
  filename: string,
  contentType: string | undefined | null,
  isDark: boolean
): string {
  const g = fileTypeGraphicPath(filename, contentType);
  if (g) return g;
  return isDark ? GENERIC_DOC_DARK : GENERIC_DOC_LIGHT;
}

/** Icon path from file extension only (sidebar file list). */
export function fileExtensionGraphicPath(
  extension: string,
  isDarkMode: boolean
): { src: string; alt: string } {
  const ext = extension.toLowerCase();
  const map: Record<string, { light: string; dark: string; alt: string }> = {
    pdf: { light: FILE_TYPE_GRAPHICS.pdf, dark: FILE_TYPE_GRAPHICS.pdf, alt: 'PDF' },
    doc: { light: FILE_TYPE_GRAPHICS.docx, dark: FILE_TYPE_GRAPHICS.docx, alt: 'Word' },
    docx: { light: FILE_TYPE_GRAPHICS.docx, dark: FILE_TYPE_GRAPHICS.docx, alt: 'Word' },
    csv: { light: FILE_TYPE_GRAPHICS.csv, dark: FILE_TYPE_GRAPHICS.csv, alt: 'CSV' },
    xls: { light: FILE_TYPE_GRAPHICS.xls, dark: FILE_TYPE_GRAPHICS.xls, alt: 'Excel' },
    xlsx: { light: FILE_TYPE_GRAPHICS.xls, dark: FILE_TYPE_GRAPHICS.xls, alt: 'Excel' },
    jpg: { light: GENERIC_DOC_LIGHT, dark: GENERIC_DOC_DARK, alt: 'Image' },
    jpeg: { light: GENERIC_DOC_LIGHT, dark: GENERIC_DOC_DARK, alt: 'Image' },
    png: { light: GENERIC_DOC_LIGHT, dark: GENERIC_DOC_DARK, alt: 'Image' },
  };
  const icon = map[ext] || {
    light: GENERIC_DOC_LIGHT,
    dark: GENERIC_DOC_DARK,
    alt: 'Document',
  };
  return { src: isDarkMode ? icon.dark : icon.light, alt: icon.alt };
}

/** Full filename → icon (for file pickers / lists). */
export function fileIconFromFileName(fileName: string, isDarkMode: boolean): { src: string; alt: string } {
  return fileExtensionGraphicPath(fileName.split('.').pop()?.toLowerCase() || '', isDarkMode);
}
